import { useState, useEffect } from "react";
import { Send, FileCheck, Banknote, PackagePlus, ExternalLink, Truck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// Chantier 3b : UNE LIGNE DE BLOCAGE = UN BOUTON QUI LA RÉSOUT.
export const ACTIONS_BLOCAGE = {
  PIECE_MANQUANTE:          { label: "Réclamer au client", type: "message", icone: Send },
  ASSURANCE_ABSENTE:        { label: "Réclamer au client", type: "message", icone: Send },
  SIGNATURE_EN_ATTENTE:     { label: "Relancer la signature", type: "message", icone: Send },
  CG_NON_RETIREE:           { label: "Prévenir le client", type: "message", icone: Send },
  SOLDE_NON_ENCAISSE:       { label: "Relancer le règlement", type: "message", icone: Banknote },
  DATE_USINE_DEPASSEE:      { label: "Informer le client du report", type: "message", icone: Send },
  CG_NON_DEMANDEE:          { label: "Déclarer la demande déposée", type: "cg_deposee", icone: FileCheck },
  LIVRE_NON_FACTURE:        { label: "Facturer", type: "facturer", icone: Banknote },
  REPRISE_NON_ENTREE_STOCK: { label: "Entrer la reprise en stock", type: "reprise_stock", icone: PackagePlus },
  VEHICULE_NON_AFFECTE:     { label: "Ouvrir la vente", type: "lien_vente", icone: ExternalLink },
  PREPARATION_EN_RETARD:    { label: "Voir le planning atelier", type: "lien_planning", icone: Truck },
};

export function MessageBlocageDialog({ dossierId, code, onClose, onEnvoye }) {
  const [msg, setMsg] = useState(null);
  const [canal, setCanal] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    api.get(`/dossiers/${dossierId}/action-blocage/${code}`)
      .then((r) => {
        setMsg(r.data);
        setCanal(r.data.canaux_autorises?.[0] || null);
      })
      .catch((e) => { toast.error(apiErr(e)); onClose(); });
    // eslint-disable-next-line
  }, [dossierId, code]);

  const envoyer = async () => {
    setEnvoi(true);
    try {
      const r = await api.post(`/dossiers/${dossierId}/action-blocage/${code}`,
        { canal, sujet: msg.sujet, contenu: msg.contenu });
      toast.success(`Message ${canal} : ${r.data.statut}`);
      onEnvoye && onEnvoye();
      onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setEnvoi(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="message-blocage-dialog">
        <DialogHeader>
          <DialogTitle>{ACTIONS_BLOCAGE[code]?.label || "Message au client"}</DialogTitle>
          <DialogDescription>Message pré-rédigé — relisez et modifiez avant envoi. Le consentement RGPD est respecté.</DialogDescription>
        </DialogHeader>
        {!msg ? <p className="text-[13px] text-[--idc-muted]">Préparation du message…</p> : (
          <div className="space-y-3">
            {msg.canaux_autorises.length === 0 ? (
              <p className="text-[13px] text-[#B91C1C] font-medium" data-testid="message-aucun-canal">
                Aucun canal autorisé pour ce client (consentement RGPD ou coordonnées manquantes).
              </p>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-[--idc-muted]">Canal :</span>
                  {msg.canaux_autorises.map((c) => (
                    <label key={c} className="flex items-center gap-1.5 text-[13px]">
                      <input type="radio" checked={canal === c} onChange={() => setCanal(c)}
                        data-testid={`message-canal-${c}`} />
                      {c === "email" ? `E-mail (${msg.destinataires.email})` : `SMS (${msg.destinataires.sms})`}
                    </label>
                  ))}
                </div>
                {canal === "email" && (
                  <input value={msg.sujet || ""} onChange={(e) => setMsg({ ...msg, sujet: e.target.value })}
                    className="w-full text-[13px] font-semibold border border-[--idc-border] rounded px-2 py-1.5"
                    data-testid="message-sujet" />
                )}
                <textarea rows={10} value={msg.contenu} onChange={(e) => setMsg({ ...msg, contenu: e.target.value })}
                  className="w-full text-[13px] border border-[--idc-border] rounded p-2 font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#2F6FED]/30"
                  data-testid="message-contenu" />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={onClose} data-testid="message-annuler">Annuler</Button>
                  <Button onClick={envoyer} disabled={envoi || !canal || !msg.contenu.trim()}
                    data-testid="message-envoyer">
                    <Send className="w-3.5 h-3.5 mr-1.5" /> {envoi ? "Envoi…" : "Envoyer"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Hook d'exécution des actions non-message. Retourne { executer, refus, setRefus }.
export function useActionBlocage({ dossierId, onDone }) {
  const navigate = useNavigate();
  const [refus, setRefus] = useState(null); // { controle, message, manques }
  const [messageCode, setMessageCode] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const executer = async (code) => {
    const action = ACTIONS_BLOCAGE[code];
    if (!action) return;
    if (action.type === "message") { setMessageCode(code); return; }
    if (action.type === "lien_vente") { navigate(`/ventes/${dossierId}`); return; }
    if (action.type === "lien_planning") { navigate("/planning"); return; }
    setEnCours(true);
    try {
      if (action.type === "cg_deposee") {
        await api.patch(`/dossiers/${dossierId}/immatriculation`, {
          valeurs: { statut: "demande_deposee", date_depot_demande: new Date().toISOString().slice(0, 10) },
        });
        toast.success("Demande d'immatriculation déclarée déposée.");
      } else if (action.type === "facturer") {
        const r = await api.post(`/dossiers/${dossierId}/facturer`, {});
        toast.success(`Facture ${r.data.numero} émise.`);
      } else if (action.type === "reprise_stock") {
        const r = await api.post(`/dossiers/${dossierId}/entrer-reprise-stock`);
        toast.success(`Reprise entrée au stock (${r.data.vehicule?.numero_police || "VO"}).`);
      }
      onDone && onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.manques) {
        setRefus(d); // compte rendu du contrôle : liste nommée
      } else {
        toast.error(apiErr(e));
      }
    } finally { setEnCours(false); }
  };

  return { executer, refus, setRefus, messageCode, setMessageCode, enCours };
}
